import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2,
  User,
  Mail,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Check,
  Warehouse,
  ShieldCheck,
} from "lucide-react";

import Logo from "src/components/common/Logo";
import Input from "src/components/ui/Input";
import Button from "src/components/ui/Button";
import AuthBrandPanel from "src/components/auth/AuthBrandPanel";
import AuthThemeToggle from "src/components/auth/AuthThemeToggle";
import IdentifierField from "src/components/auth/IdentifierField";
import PasswordField from "src/components/auth/PasswordField";
import {
  LOCATION_LABEL_PRESETS,
  DEFAULT_LOCATION_LABEL_PRESET,
} from "src/constants/locationLabelPresets";
import {
  validateRequiredName,
  validateEmail,
  validateMobile,
  validatePassword,
} from "src/utils/authFormValidation";

import { getFromStorage } from "src/utils/storage";
import storageKeys from "src/constants/storage";
import useIdentifierAvailability from "src/hooks/useIdentifierAvailability";
import * as authTenantActions from "src/redux/slices/authTenantSlice";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";

type WizardStep = 1 | 2 | 3 | 4;
const TOTAL_STEPS = 4;

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { sendSignupOtp, verifySignupOtp, completeSignup } = useAppSelector(
    (state) => state.authTenant
  );

  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const [submitted, setSubmitted] = useState(false);

  // The slide track keeps all step panels mounted side by side (for the
  // horizontal transition), which stretches the row to the tallest panel's
  // height even while showing a shorter one. Track the active panel's real
  // height and size the wrapper to that instead.
  const stepRefs = [
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
  ];
  const [trackHeight, setTrackHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const activeEl = stepRefs[wizardStep - 1].current;
    if (!activeEl) return;
    const update = () => setTrackHeight(activeEl.scrollHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(activeEl);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardStep]);

  // Step 1 — email
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  // Step 2 — verification code
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendMessage, setResendMessage] = useState("");

  // Step 3 — organization details
  const [organizationName, setOrganizationName] = useState("");
  const [organizationNameError, setOrganizationNameError] = useState("");
  const [organizationIdentifier, setOrganizationIdentifier] = useState("");
  const [identifierRequiredError, setIdentifierRequiredError] = useState("");
  const identifierAvailability = useIdentifierAvailability(organizationIdentifier);
  const [locationLabel, setLocationLabel] = useState(
    DEFAULT_LOCATION_LABEL_PRESET.label
  );

  // Step 4 — owner (admin user) details
  const [ownerName, setOwnerName] = useState("");
  const [ownerNameError, setOwnerNameError] = useState("");
  const [mobile, setMobile] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [apiError, setApiError] = useState("");

  useEffect(() => {
    const authTenantToken = getFromStorage(storageKeys.authTenantToken);
    if (authTenantToken) {
      navigate("/app/dashboard");
    }
  }, []);

  useEffect(() => {
    if (sendSignupOtp?.data && !sendSignupOtp?.error) {
      setApiError("");
      if (wizardStep === 1) setWizardStep(2);
      else setResendMessage("A new code was sent.");
    } else if (sendSignupOtp?.error) {
      if (wizardStep === 1) setEmailError(sendSignupOtp.error);
      else {
        setResendMessage("");
        setOtpError(sendSignupOtp.error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendSignupOtp]);

  useEffect(() => {
    if (verifySignupOtp?.data && !verifySignupOtp?.error) {
      setOtpError("");
      setWizardStep(3);
    } else if (verifySignupOtp?.error) {
      setOtpError(verifySignupOtp.error);
    }
  }, [verifySignupOtp]);

  useEffect(() => {
    if (completeSignup?.data && !completeSignup?.error) {
      setSubmitted(true);
    } else if (completeSignup?.error) {
      setApiError(completeSignup.error);
    }
  }, [completeSignup]);

  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault();
    const emailErr = validateEmail(email);
    setEmailError(emailErr || "");
    if (emailErr) return;
    dispatch(authTenantActions.sendSignupOtpRequest({ email }));
  };

  const handleVerifyCode = (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError("");
    if (!/^\d{6}$/.test(otpCode)) {
      setOtpError("Enter the 6-digit code");
      return;
    }
    dispatch(authTenantActions.verifySignupOtpRequest({ email, code: otpCode }));
  };

  const handleResendCode = () => {
    setResendMessage("");
    setOtpError("");
    dispatch(authTenantActions.sendSignupOtpRequest({ email }));
  };

  const handleStep3Submit = (e: React.FormEvent) => {
    e.preventDefault();
    const orgNameErr = validateRequiredName(organizationName, "Organization name");
    setOrganizationNameError(orgNameErr || "");

    if (!organizationIdentifier.trim()) {
      setIdentifierRequiredError("Organization identifier is required");
    } else {
      setIdentifierRequiredError("");
    }

    if (orgNameErr || !organizationIdentifier.trim()) return;
    if (identifierAvailability.status !== "available") return;

    setWizardStep(4);
  };

  const handleStep4Submit = (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");

    const nameErr = validateRequiredName(ownerName, "Name");
    const mobileErr = validateMobile(mobile);
    const passwordErr = validatePassword(password);
    setOwnerNameError(nameErr || "");
    setMobileError(mobileErr || "");
    setPasswordError(passwordErr || "");
    if (nameErr || mobileErr || passwordErr) return;

    const preset =
      LOCATION_LABEL_PRESETS.find((p) => p.label === locationLabel) ||
      DEFAULT_LOCATION_LABEL_PRESET;

    dispatch(
      authTenantActions.completeSignupRequest({
        email,
        organizationName,
        organizationIdentifier: organizationIdentifier
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-"),
        ownerName,
        mobile,
        password,
        warehouseLabelSingular: preset.singular,
        warehouseLabelPlural: preset.plural,
      })
    );
  };

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900">
      <AuthThemeToggle />
      <AuthBrandPanel
        badge="Request access"
        heading="Set up in minutes, not meetings."
        subtext="Tell us about your organization — our team reviews every request personally and reaches out to get you started."
      >
        <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur">
          <div className="mb-2.5 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold tracking-tight">15</span>
            <span className="text-[13px] font-semibold text-white/75">
              day free trial
            </span>
          </div>
          <ul className="space-y-1.5">
            {[
              "No card required to start",
              "Verified by our team for your security",
              "Usually reviewed within one business day",
            ].map((line) => (
              <li key={line} className="flex items-center gap-2 text-[12.5px] text-white/85">
                <Check size={13} className="shrink-0 text-emerald-300" />
                {line}
              </li>
            ))}
          </ul>
        </div>
      </AuthBrandPanel>

      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-[420px]">
          <div className="overflow-hidden rounded-[18px] border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            {submitted ? (
              <div className="p-8 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                  <CheckCircle2 size={26} />
                </div>
                <h2 className="mb-1.5 text-xl font-extrabold text-gray-900 dark:text-gray-100">
                  We've got your request
                </h2>
                <p className="mb-6 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                  Thanks for telling us about your organization. Our team
                  personally reviews every new workspace before it goes live
                  — this keeps Invyntra secure for everyone.
                </p>

                <div className="mb-6 space-y-3 text-left">
                  {[
                    ["Next", "Our sales team will call or email you, usually within one business day."],
                    ["Then", "We verify your details and activate your workspace."],
                    ["Finally", "You'll get an email to sign in and start your 15-day free trial."],
                  ].map(([label, text], i) => (
                    <div key={label} className="flex gap-2.5">
                      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[11px] font-extrabold text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                        {i + 1}
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                          {label}
                        </div>
                        {text}
                      </div>
                    </div>
                  ))}
                </div>

                <Link to="/">
                  <Button fullWidth>Back to homepage</Button>
                </Link>
                <p className="mt-3.5 text-sm text-gray-500 dark:text-gray-400">
                  Already approved?{" "}
                  <Link
                    to="/app/login"
                    className="font-semibold text-primary-600 hover:underline dark:text-primary-400"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            ) : (
              <>
                <div className="p-8 pb-5">
                  <div className="mb-5 flex items-center gap-2">
                    <Logo size="small" variant="contained" />
                    <Logo size="large" variant="text" />
                  </div>

                  {/* Progress bar — no step numbers, just an animated fill */}
                  <div className="mb-4 h-1 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                    <div
                      className="h-full rounded-full bg-primary-600 transition-[width] duration-500 ease-out"
                      style={{ width: `${(wizardStep / TOTAL_STEPS) * 100}%` }}
                    />
                  </div>

                  <h2 className="mb-1.5 text-xl font-extrabold text-gray-900 dark:text-gray-100">
                    {wizardStep === 1 && "What's your work email?"}
                    {wizardStep === 2 && "Verify your email"}
                    {wizardStep === 3 && "Your organization"}
                    {wizardStep === 4 && "Create your account"}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {wizardStep === 1 && "We'll send a code to confirm it's really you."}
                    {wizardStep === 2 && (
                      <>
                        We've sent a 6-digit code to <b>{email}</b>.
                      </>
                    )}
                    {wizardStep === 3 && "Tell us about your organization."}
                    {wizardStep === 4 && "Last step — set up your login."}
                  </p>
                </div>

                {apiError && (
                  <div className="mx-8 mb-4 rounded-lg bg-red-100 p-2.5 text-center text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
                    {apiError}
                  </div>
                )}

                {/* Slide track: 4 panels, 400% total width */}
                <div
                  className="overflow-hidden transition-[height] duration-300 ease-out"
                  style={{ height: trackHeight }}
                >
                  <div
                    className="flex items-start transition-transform duration-300 ease-out"
                    style={{
                      width: "400%",
                      transform: `translateX(-${(wizardStep - 1) * 25}%)`,
                    }}
                  >
                    {/* Step 1 — email */}
                    <div ref={stepRefs[0]} className="w-1/4 px-8 pb-8">
                      <form className="space-y-3.5" onSubmit={handleStep1Submit}>
                        <Input
                          label="Work email"
                          required
                          type="email"
                          placeholder="you@company.com"
                          value={email}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEmail(v);
                            setEmailError(validateEmail(v) || "");
                          }}
                          startAdornment={<Mail size={16} />}
                          error={!!emailError}
                          helperText={emailError || undefined}
                        />
                        <Button
                          type="submit"
                          fullWidth
                          isLoading={sendSignupOtp?.loading && wizardStep === 1}
                          className="mt-1.5"
                        >
                          Verify and continue
                          <ArrowRight size={15} className="ml-1.5" />
                        </Button>
                      </form>

                      <div className="my-5 flex items-center gap-2.5 text-xs text-gray-400 dark:text-gray-500">
                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                        already set up
                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                      </div>
                      <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                        Already have a workspace?{" "}
                        <Link
                          to="/app/login"
                          className="font-semibold text-primary-600 hover:underline dark:text-primary-400"
                        >
                          Sign in
                        </Link>
                      </p>
                    </div>

                    {/* Step 2 — verification code */}
                    <div ref={stepRefs[1]} className="w-1/4 px-8 pb-8">
                      <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                        <ShieldCheck size={22} />
                      </div>
                      <form onSubmit={handleVerifyCode} className="space-y-3.5">
                        <Input
                          label="Verification code"
                          required
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="123456"
                          value={otpCode}
                          onChange={(e) => {
                            setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                            setOtpError("");
                          }}
                          error={!!otpError}
                          helperText={otpError || undefined}
                          inputClassName="text-center tracking-[0.4em]"
                        />
                        <div className="flex gap-2.5">
                          <button
                            type="button"
                            onClick={() => setWizardStep(1)}
                            className="flex items-center gap-1 px-3 text-sm font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          >
                            <ArrowLeft size={15} />
                            Back
                          </button>
                          <Button
                            type="submit"
                            fullWidth
                            isLoading={verifySignupOtp?.loading}
                          >
                            Verify and continue
                            <ArrowRight size={15} className="ml-1.5" />
                          </Button>
                        </div>
                      </form>

                      {resendMessage && (
                        <p className="mt-3.5 text-center text-sm text-emerald-600 dark:text-emerald-400">
                          {resendMessage}
                        </p>
                      )}
                      <p className="mt-3.5 text-center text-sm text-gray-500 dark:text-gray-400">
                        Didn't get it?{" "}
                        <button
                          type="button"
                          onClick={handleResendCode}
                          disabled={sendSignupOtp?.loading}
                          className="font-semibold text-primary-600 hover:underline disabled:opacity-50 dark:text-primary-400"
                        >
                          Resend code
                        </button>
                      </p>
                    </div>

                    {/* Step 3 — organization details */}
                    <div ref={stepRefs[2]} className="w-1/4 px-8 pb-8">
                      <form className="space-y-3.5" onSubmit={handleStep3Submit}>
                        <Input
                          label="Organization name"
                          required
                          placeholder="Your Company Name"
                          value={organizationName}
                          onChange={(e) => {
                            const v = e.target.value;
                            setOrganizationName(v);
                            setOrganizationNameError(
                              validateRequiredName(v, "Organization name") || ""
                            );
                          }}
                          startAdornment={<Building2 size={16} />}
                          error={!!organizationNameError}
                          helperText={organizationNameError || undefined}
                        />
                        <IdentifierField
                          value={organizationIdentifier}
                          onChange={(v) => {
                            setOrganizationIdentifier(v);
                            setIdentifierRequiredError("");
                          }}
                          status={identifierAvailability.status}
                          suggestions={identifierAvailability.suggestions}
                          error={identifierRequiredError || identifierAvailability.error}
                        />

                        <div className="flex flex-col items-start space-y-1">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            How do you name your location?
                          </label>
                          <div className="flex w-full items-center gap-2 rounded-md border border-gray-300 bg-white px-2 dark:border-gray-700 dark:bg-gray-800">
                            <span className="text-gray-500 dark:text-gray-400">
                              <Warehouse size={16} />
                            </span>
                            <select
                              className="h-9 w-full flex-1 bg-transparent text-sm text-gray-900 outline-none dark:text-gray-100"
                              value={locationLabel}
                              onChange={(e) => setLocationLabel(e.target.value)}
                            >
                              {LOCATION_LABEL_PRESETS.map((p) => (
                                <option key={p.label} value={p.label}>
                                  {p.label} / {p.plural}
                                </option>
                              ))}
                            </select>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Used everywhere the app refers to a warehouse. You can
                            change this later too.
                          </span>
                        </div>

                        <div className="flex gap-2.5">
                          <button
                            type="button"
                            onClick={() => setWizardStep(2)}
                            className="flex items-center gap-1 px-3 text-sm font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          >
                            <ArrowLeft size={15} />
                            Back
                          </button>
                          <Button
                            type="submit"
                            fullWidth
                            disabled={
                              !!organizationIdentifier &&
                              identifierAvailability.status !== "available"
                            }
                          >
                            Next
                            <ArrowRight size={15} className="ml-1.5" />
                          </Button>
                        </div>
                      </form>
                    </div>

                    {/* Step 4 — owner (admin user) details */}
                    <div ref={stepRefs[3]} className="w-1/4 px-8 pb-8">
                      <form className="space-y-3.5" onSubmit={handleStep4Submit}>
                        <Input
                          label="Your name"
                          required
                          placeholder="Jane Doe"
                          value={ownerName}
                          onChange={(e) => {
                            const v = e.target.value;
                            setOwnerName(v);
                            setOwnerNameError(validateRequiredName(v, "Name") || "");
                          }}
                          startAdornment={<User size={16} />}
                          error={!!ownerNameError}
                          helperText={ownerNameError || undefined}
                        />
                        <Input
                          label="Mobile number"
                          required
                          placeholder="98765 43210"
                          value={mobile}
                          onChange={(e) => {
                            const v = e.target.value;
                            setMobile(v);
                            setMobileError(validateMobile(v) || "");
                          }}
                          startAdornment={<span className="text-sm">+91</span>}
                          error={!!mobileError}
                          helperText={mobileError || undefined}
                        />
                        <PasswordField
                          value={password}
                          onChange={(v) => {
                            setPassword(v);
                            setPasswordError(validatePassword(v) || "");
                          }}
                          error={passwordError}
                        />

                        <div className="flex gap-2.5">
                          <button
                            type="button"
                            onClick={() => setWizardStep(3)}
                            className="flex items-center gap-1 px-3 text-sm font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          >
                            <ArrowLeft size={15} />
                            Back
                          </button>
                          <Button
                            type="submit"
                            fullWidth
                            isLoading={completeSignup?.loading}
                          >
                            Submit for review
                            <ArrowRight size={15} className="ml-1.5" />
                          </Button>
                        </div>
                        <p className="text-center text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">
                          By continuing you agree to Invyntra's Terms and Privacy
                          Policy. Every new workspace is manually verified before
                          activation.
                        </p>
                      </form>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
